import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PlatformShell from '../../components/PlatformShell';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { StatCard, DataTable } from '../../components/Ui';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: '7 days' },
  { id: 'month', label: 'This month' },
  { id: 'year', label: 'This year' },
  { id: 'all', label: 'All time' },
];

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

export default function PlatformAnalytics() {
  const [searchParams] = useSearchParams();
  const [period, setPeriod] = useState('month');
  const [hospitalId, setHospitalId] = useState(searchParams.get('hospitalId') || '');
  const { data: hospData } = useLiveData(() => ipApi.platformHospitals(), []);
  const hospitalsList = hospData?.hospitals || [];
  const { data, loading, error } = useLiveData(
    () => ipApi.platformAnalytics({ period, hospitalId: hospitalId || undefined }),
    [period, hospitalId]
  );

  const totals = data?.totals || {};
  const hospitals = data?.hospitals || [];

  const columns = [
    {
      key: 'name',
      label: 'Hospital',
      render: (r) => (
        <Link to={`/platform/hospitals/${r.hospitalId}`}>{r.name}</Link>
      ),
    },
    { key: 'plan', label: 'Plan' },
    { key: 'opReceiptsTotal', label: 'OP collected', render: (r) => formatInr(r.opReceiptsTotal) },
    { key: 'receiptCount', label: 'Receipts' },
    { key: 'ipAdmissions', label: 'IP active' },
    { key: 'ipOutstanding', label: 'IP outstanding', render: (r) => formatInr(r.ipOutstanding) },
    { key: 'newPatients', label: 'New patients' },
    {
      key: 'actions',
      label: '',
      render: (r) => <Link to={`/platform/hospitals/${r.hospitalId}?tab=revenue`} className="btn btn-sm btn-secondary">Details</Link>,
    },
  ];

  return (
    <PlatformShell title="Platform analytics" subtitle="Revenue & activity across all hospitals">
      <div className="platform-filters card mb">
        <select className="input" value={hospitalId} onChange={(e) => setHospitalId(e.target.value)}>
          <option value="">All hospitals</option>
          {hospitalsList.map((h) => (
            <option key={h.hospitalId} value={h.hospitalId}>{h.name}</option>
          ))}
        </select>
      </div>

      <div className="period-tabs card mb">
        {PERIODS.map((p) => (
          <button key={p.id} type="button" className={`emr-chip ${period === p.id ? 'active' : ''}`} onClick={() => setPeriod(p.id)}>
            {p.label}
          </button>
        ))}
      </div>

      {error && <div className="error mb">{error}</div>}

      <div className="stat-grid">
        <StatCard label="OP collected" value={loading ? '…' : formatInr(totals.opReceiptsTotal)} accent="#10b981" />
        <StatCard label="All receipts" value={loading ? '…' : formatInr(totals.receiptsTotal)} accent="#4f8ef7" />
        <StatCard label="Receipt count" value={loading ? '…' : totals.receiptCount ?? 0} />
        <StatCard label="IP outstanding" value={loading ? '…' : formatInr(totals.ipOutstanding)} accent="#f97316" />
        <StatCard label="Active IP" value={loading ? '…' : totals.ipAdmissions ?? 0} />
        <StatCard label="New patients" value={loading ? '…' : totals.newPatients ?? 0} accent="#8b5cf6" />
      </div>

      <div className="card mt">
        <h3>Per hospital {hospitalId ? '— filtered' : ''}</h3>
        <DataTable columns={columns} rows={hospitals} keyField="hospitalId" loading={loading} />

      </div>
    </PlatformShell>
  );
}
